import * as d3 from "d3"

export const LEGEND_REGION_TEXT_MARKER_GAP = 4
export const LEGEND_REGION_COLUMN_GAP = 14
export const LEGEND_ROW_HEIGHT_MULTIPLIER = 1.36

export function appendWrappedLegendText(
  legendTextGroup: d3.Selection<SVGGElement, unknown, any, any>,
  x: number,
  y: number,
  text: string,
  maxWidth: number,
  lineHeight: number
) {
  if (!text) {
    return 0
  }

  const textSelection = legendTextGroup
    .append("text")
    .attr("x", x)
    .attr("y", y)

  if (maxWidth <= 0) {
    textSelection.text(text)
    return 1
  }

  const words = text.split(/\s+/).filter(Boolean)
  if (words.length === 0) {
    return 0
  }

  let line: string[] = []
  let lineCount = 0
  let tspan = textSelection
    .append("tspan")
    .attr("x", x)
    .attr("dy", 0)

  words.forEach((word: string) => {
    line.push(word)
    tspan.text(line.join(" "))

    if (line.length > 1 && (tspan.node()?.getComputedTextLength() ?? 0) > maxWidth) {
      line.pop()
      tspan.text(line.join(" "))
      line = [word]
      lineCount += 1
      tspan = textSelection
        .append("tspan")
        .attr("x", x)
        .attr("dy", lineHeight)
        .text(word)
    }
  })

  return lineCount + 1
}

export function getLegendRegionEntryWidth(
  textWidth: number,
  circleRadius: number
) {
  return textWidth + circleRadius * 2 + LEGEND_REGION_TEXT_MARKER_GAP
}

export function splitLegendRegionEntriesIntoRows<
  Entry extends { color: string; text: string }
>(
  regionEntries: Array<Entry>,
  maxWidth: number,
  preferredMaxItemsPerRow: number,
  circleRadius: number,
  getTextWidth: (text: string) => number
) {
  const chunkEntries = (itemsPerRow: number) => {
    const rows: Array<Array<Entry>> = []

    for (let index = 0; index < regionEntries.length; index += itemsPerRow) {
      rows.push(regionEntries.slice(index, index + itemsPerRow))
    }

    return rows
  }

  for (let itemsPerRow = preferredMaxItemsPerRow; itemsPerRow >= 1; itemsPerRow -= 1) {
    const rows = chunkEntries(itemsPerRow)
    const columnWidths = getLegendRegionColumnWidths(rows, circleRadius, getTextWidth)
    const totalWidth = columnWidths.reduce((width: number, columnWidth: number) => {
      return width + columnWidth
    }, 0) + Math.max(columnWidths.length - 1, 0) * LEGEND_REGION_COLUMN_GAP

    if (totalWidth <= maxWidth || itemsPerRow === 1) {
      return rows
    }
  }

  return []
}

export function getLegendRegionColumnWidths(
  regionRows: Array<Array<{ color: string; text: string }>>,
  circleRadius: number,
  getTextWidth: (text: string) => number
): number[] {
  const columnCount = regionRows.reduce((maxCount: number, row) => {
    return Math.max(maxCount, row.length)
  }, 0)
  const columnWidths = new Array<number>(columnCount).fill(0)

  regionRows.forEach((row) => {
    const firstColumnIndex = columnCount - row.length

    row.forEach((entry, entryIndex) => {
      const columnIndex = firstColumnIndex + entryIndex
      columnWidths[columnIndex] = Math.max(
        columnWidths[columnIndex],
        getLegendRegionEntryWidth(getTextWidth(entry.text), circleRadius)
      )
    })
  })

  return columnWidths
}